import { Injectable } from '@angular/core';
import { InternalApiService } from './internal-api.service';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';

declare let gtag: Function;

export interface CustomEventData {
  event_type: string;
  event_data?: any;
  page_url?: string;
  session_id?: string;
}

export interface UserActivity {
  session_count?: number;
  page_views?: number;
  mindmaps_created?: number;
  files_uploaded?: number;
  time_spent_minutes?: number;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private sessionId: string = '';
  private currentSessionStartTime: Date = new Date();
  private pageViewCount: number = 0;
  private isGA4Loaded: boolean = false;
  private eventQueue: any[] = []; // Queue events until GA4 loads

  constructor(
    private apiService: InternalApiService,
    private authService: AuthService
  ) {
    this.generateSessionId();
    this.startUserSession();
    
    // Make debug method available globally for debugging
    (window as any).debugAnalyticsUserId = () => this.debugUserIdInBackend();
    (window as any).debugGA4Status = () => this.debugGA4Status();
    console.log('🔧 Debug methods available:');
    console.log('  - window.debugAnalyticsUserId()');
    console.log('  - window.debugGA4Status()');
    
    // Subscribe to auth state changes to set/clear user ID
    this.authService.authState$.subscribe((isLoggedIn: boolean) => {
      if (isLoggedIn) {
        const user = this.authService.getUserInfo();
        if (user) {
          this.setUserId(this.getUserIdForGA4(user));
          this.trackUserLogin(user);
        }
      } else {
        this.clearUserId();
        this.trackUserLogout();
      }
    });
  }

  // NEW: Debug GA4 status
  private debugGA4Status(): void {
    console.log('🔧 GA4 Debug Status:', {
      isGA4Loaded: this.isGA4Loaded,
      gtagExists: typeof gtag !== 'undefined',
      measurementId: environment.ga4MeasurementId,
      hostname: window.location.hostname,
      currentURL: window.location.href,
      eventQueueLength: this.eventQueue.length,
      sessionId: this.sessionId
    });
    
    // Check if gtag is available globally
    if (typeof (window as any).gtag === 'function') {
      console.log('✅ Global gtag function is available');
    } else {
      console.log('❌ Global gtag function is NOT available');
    }
    
    // Check dataLayer
    if ((window as any).dataLayer) {
      console.log('✅ dataLayer exists:', (window as any).dataLayer.length, 'items');
    } else {
      console.log('❌ dataLayer does NOT exist');
    }
  }

  // Initialize GA4
  initializeGA4(measurementId: string): void {
    console.log('🚀 Initializing GA4 with ID:', measurementId);
    
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script1);

    script1.onload = () => {
      console.log('📄 GA4 script loaded, setting up gtag...');
      
      const script2 = document.createElement('script');
      script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        
        // Check if we're in development mode
        const isLocalhost = window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1' || 
                           window.location.hostname.includes('localhost');
        
        gtag('config', '${measurementId}', {
          cookie_flags: 'SameSite=None;Secure',
          send_page_view: false, // We'll handle page views manually
          debug_mode: isLocalhost, // Enable debug mode for localhost
          allow_google_signals: true,
          allow_ad_personalization_signals: true,
          // Allow events from any domain (important for localhost testing)
          cookie_domain: 'auto',
          // Disable automatic page view tracking
          page_title: document.title,
          page_location: window.location.href,
          // Ensure User ID is properly handled
          anonymize_ip: false,
          // Set custom parameters for anonymous users
          custom_map: {
            'custom_parameter_1': 'user_status'
          }
        });
        
        console.log('🔧 GA4 Configuration complete:', {
          measurementId: '${measurementId}',
          hostname: window.location.hostname,
          isLocalhost: isLocalhost,
          debugMode: isLocalhost,
          currentURL: window.location.href
        });
      `;
      document.head.appendChild(script2);
      
      // Wait a bit for gtag to be fully available
      setTimeout(() => {
        this.isGA4Loaded = true;
        console.log('✅ GA4 fully initialized and ready');
        
        // Process queued events
        this.processEventQueue();
        
        // Set user properties if user is logged in
        const user = this.authService.getUserInfo();
        if (user) {
          this.setUserProperties(user);
        }
        
        // Send initial page view
        this.sendInitialPageView();
      }, 100);
    };

    script1.onerror = () => {
      console.error('❌ Failed to load GA4 script');
    };
  }

  // NEW: Send initial page view after GA4 is loaded
  private sendInitialPageView(): void {
    const currentUrl = window.location.pathname;
    const currentTitle = this.getPageTitle(currentUrl);
    
    console.log('🚀 === INITIAL PAGE VIEW DEBUG ===');
    console.log('📊 Sending initial page view:', { title: currentTitle, url: currentUrl });
    
    // Check if user is logged in
    const isLoggedIn = this.authService.isLoggedIn();
    const user = this.authService.getUserInfo();
    
    console.log('🔍 User Login Status:', {
      isLoggedIn: isLoggedIn,
      hasUser: !!user,
      userDetails: user
    });
    
    if (isLoggedIn && user) {
      // User is logged in - set their User ID
      const userId = this.getUserIdForGA4(user);
      console.log('✅ User is logged in, setting User ID:', userId);
      this.setUserId(userId);
    } else {
      // User is anonymous - explicitly clear User ID
      console.log('👤 User is anonymous, ensuring no User ID is set');
      this.clearUserId();
    }
    
    // Send page view with appropriate user context
    this.trackPageView(currentTitle, currentUrl);
    
    // Track anonymous user if not logged in
    if (!isLoggedIn) {
      console.log('🎯 === ANONYMOUS USER TRACKING ===');
      console.log('👤 About to track anonymous user app launch');
      console.log('🔧 GA4 Status before anonymous tracking:', {
        isGA4Loaded: this.isGA4Loaded,
        gtagExists: typeof gtag !== 'undefined'
      });
      
      this.trackAnonymousUserAction('app_launch', {
        landing_page: currentUrl,
        timestamp: new Date().toISOString(),
        user_status: 'anonymous'
      });
      
      console.log('✅ Anonymous user tracking call completed');
    } else {
      console.log('ℹ️ User is logged in, skipping anonymous tracking');
    }
    console.log('🏁 === INITIAL PAGE VIEW DEBUG END ===');
  }

  // NEW: Get page title helper
  private getPageTitle(url: string): string {
    if (url === '/' || url === '/home') return 'Home';
    if (url.includes('/login')) return 'Login';
    if (url.includes('/dashboard')) return 'Dashboard';
    if (url.includes('/mindmap')) return 'Mind Map';
    if (url.includes('/quiz')) return 'Quiz';
    if (url.includes('/upload')) return 'File Upload';
    if (url.includes('/profile')) return 'Profile';
    return url.split('/').pop() || 'Unknown Page';
  }

  // NEW: Process event queue when GA4 is ready
  private processEventQueue(): void {
    console.log('🔄 Processing', this.eventQueue.length, 'queued events');
    
    while (this.eventQueue.length > 0) {
      const event = this.eventQueue.shift();
      this.sendGtagEvent(event.eventName, event.parameters);
    }
  }

  // NEW: Queue or send events based on GA4 status
  private sendGtagEvent(eventName: string, parameters: any): void {
    if (!this.isGA4Loaded) {
      console.log('⏳ Queueing event (GA4 not ready):', eventName, parameters);
      this.eventQueue.push({ eventName, parameters });
      return;
    }

    if (typeof gtag === 'undefined') {
      console.error('❌ gtag function not available');
      return;
    }

    // Enhanced debugging
    console.group(`📤 Sending GA4 Event: ${eventName}`);
    console.log('📊 Event Parameters:', JSON.stringify(parameters, null, 2));
    console.log('🔧 GA4 Status:', {
      isLoaded: this.isGA4Loaded,
      measurementId: environment.ga4MeasurementId,
      hostname: window.location.hostname
    });

    // Ensure anonymous users have explicit user_id handling
    const isLoggedIn = this.authService.isLoggedIn();
    if (!isLoggedIn && !parameters.user_id) {
      console.log('👤 User Status: ANONYMOUS');
    } else {
      console.log('✅ User Status: LOGGED IN');
    }
    
    try {
      // Monitor dataLayer before sending
      console.log('📋 dataLayer before event:', (window as any).dataLayer?.length || 'undefined');
      
      gtag('event', eventName, parameters);
      
      // Monitor dataLayer after sending  
      setTimeout(() => {
        console.log('📋 dataLayer after event:', (window as any).dataLayer?.length || 'undefined');
        console.log('📋 Latest dataLayer entries:', (window as any).dataLayer?.slice(-3) || 'undefined');
      }, 100);
      
      console.log('✅ Event sent successfully');
      
      // Additional debug: Check if the event made it to dataLayer
      if ((window as any).dataLayer) {
        const latestEntry = (window as any).dataLayer[(window as any).dataLayer.length - 1];
        console.log('📋 Latest dataLayer entry:', latestEntry);
      }
      
    } catch (error) {
      console.error('❌ Error sending event:', error);
    }
    console.groupEnd();
  }

  // Generate unique session ID
  private generateSessionId(): void {
    this.sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Helper method to get the correct user ID for backend requests
  private getUserIdForBackend(): string | null {
    const user = this.authService.getUserInfo();
    if (!user) return null;

    // First try to get user ID from JWT token (this is what backend likely expects)
    const sessionToken = (this.authService as any).googleAuthService?.getSessionToken();
    if (sessionToken) {
      try {
        const tokenParts = sessionToken.split('.');
        const payload = JSON.parse(atob(tokenParts[1]));
        const jwtUserId = payload.user_id || payload.sub || payload.id;
        if (jwtUserId) {
          console.log('🔍 Using JWT user ID:', jwtUserId);
          return jwtUserId;
        }
      } catch (e) {
        console.warn('🔍 Could not decode JWT token for user ID:', e);
      }
    }

    // Fallback to user object properties
    let userId = user.id || user.sub || user.google_sub;
    if (userId) {
      console.log('🔍 Using user object ID:', userId);
      return userId;
    }

    // Final fallback to email
    if (user.email) {
      console.log('🔍 Using email as user ID:', user.email);
      return user.email;
    }

    console.warn('🔍 No user ID found, using timestamp fallback');
    return `user_${Date.now()}`;
  }

  // Set user properties in GA4
  setUserProperties(user: any): void {
    if (!this.isGA4Loaded) return;

    const userId = this.getUserIdForGA4(user);
    
    gtag('set', {
      user_id: userId,
      custom_map: {
        dimension1: user.role,
        dimension2: user.email
      }
    });

    gtag('set', 'user_properties', {
      user_role: user.role,
      registration_date: user.created_at,
      last_login: user.last_login,
      is_logged_in: true
    });
  }

  // NEW: Get user ID for GA4 (must not be PII like email)
  private getUserIdForGA4(user: any): string {
    // Use non-PII user ID for GA4 compliance
    return user.id || user.sub || user.google_sub || `user_${Date.now()}`;
  }

  // NEW: Set User ID for logged-in users
  setUserId(userId: string): void {
    if (!userId) return;
    
    if (this.isGA4Loaded) {
      gtag('config', environment.ga4MeasurementId, {
        user_id: userId
      });
      console.log('✅ GA4 User ID set:', userId);
    } else {
      console.log('⏳ Queueing User ID (GA4 not ready):', userId);
      // Queue this for when GA4 loads
      this.eventQueue.push({ 
        eventName: 'config', 
        parameters: { 
          config_id: environment.ga4MeasurementId,
          user_id: userId 
        } 
      });
    }
  }

  // NEW: Clear User ID when user logs out
  clearUserId(): void {
    if (this.isGA4Loaded) {
      gtag('config', environment.ga4MeasurementId, {
        user_id: "NULL"  // Set to string "NULL" for easy filtering
      });
      console.log('✅ GA4 User ID set to NULL for anonymous user');
    }
  }

  // NEW: Track user login event
  trackUserLogin(user: any): void {
    const userId = this.getUserIdForGA4(user);
    
    console.log('🔑 Tracking user login:', { userId, role: user.role });
    
    // GA4 login event
    this.sendGtagEvent('login', {
      method: user.provider || 'google',
      user_id: userId,
      user_role: user.role,
      session_id: this.sessionId
    });

    // Custom tracking
    this.trackCustomEvent({
      event_type: 'user_login',
      event_data: {
        user_id: userId,
        method: user.provider,
        user_role: user.role
      },
      session_id: this.sessionId
    });
  }

  // NEW: Track user logout event
  trackUserLogout(): void {
    console.log('🚪 Tracking user logout');
    
    // GA4 logout event
    this.sendGtagEvent('logout', {
      session_id: this.sessionId
    });

    // Custom tracking
    this.trackCustomEvent({
      event_type: 'user_logout',
      event_data: {},
      session_id: this.sessionId
    });
  }

  // NEW: Track anonymous user behavior
  trackAnonymousUserAction(action: string, data?: any): void {
    console.group(`🔥 === ANONYMOUS USER ACTION: ${action} ===`);
    console.log('📊 Action Data:', data);
    console.log('🔧 Service State:', {
      isGA4Loaded: this.isGA4Loaded,
      sessionId: this.sessionId,
      eventQueueLength: this.eventQueue.length
    });
    console.log('🔧 User Check:', {
      isLoggedIn: this.authService.isLoggedIn(),
      userInfo: this.authService.getUserInfo()
    });
    
    console.log('📤 About to call sendGtagEvent with:', {
      eventName: 'anonymous_user_action',
      parameters: {
        event_category: 'anonymous_engagement',
        event_label: action,
        session_id: this.sessionId,
        user_status: 'anonymous',
        action_type: action,
        ...data
      }
    });
    
    // GA4 anonymous user event
    this.sendGtagEvent('anonymous_user_action', {
      event_category: 'anonymous_engagement',
      event_label: action,
      session_id: this.sessionId,
      user_status: 'anonymous',
      action_type: action,
      ...data
    });

    console.log('✅ sendGtagEvent call completed');

    // Custom tracking
    console.log('📊 About to call trackCustomEvent...');
    this.trackCustomEvent({
      event_type: 'anonymous_user_action',
      event_data: {
        action: action,
        ...data
      },
      session_id: this.sessionId
    });
    console.log('✅ trackCustomEvent call completed');
    console.groupEnd();
  }

  // Track page views
  trackPageView(page_title: string, page_url: string): void {
    this.pageViewCount++;
    
    console.log('📄 Tracking page view:', { title: page_title, url: page_url, count: this.pageViewCount });
    
    // Determine user login status
    const isLoggedIn = this.authService.isLoggedIn();
    const user = this.authService.getUserInfo();
    const userStatus = isLoggedIn && user ? 'logged_in' : 'anonymous';
    
    // GA4 page view with user status
    this.sendGtagEvent('page_view', {
      page_title: page_title,
      page_location: window.location.href,
      page_path: page_url,
      session_id: this.sessionId,
      user_status: userStatus,
      // Add custom parameter to help with segmentation
      is_anonymous_user: !isLoggedIn
    });

    // Custom tracking
    this.trackCustomEvent({
      event_type: 'page_view',
      event_data: {
        page_title: page_title,
        page_url: page_url,
        page_view_count: this.pageViewCount,
        user_status: userStatus
      },
      page_url: page_url,
      session_id: this.sessionId
    });
  }

  // Track mindmap creation
  trackMindmapCreated(mindmapData: any): void {
    console.log('🗺️ Tracking mindmap creation:', mindmapData.title);
    
    // GA4 custom event
    this.sendGtagEvent('mindmap_created', {
      event_category: 'engagement',
      event_label: mindmapData.title || 'untitled',
      value: 1,
      session_id: this.sessionId
    });

    // Custom tracking
    this.trackCustomEvent({
      event_type: 'mindmap_created',
      event_data: {
        mindmap_id: mindmapData.id,
        title: mindmapData.title,
        node_count: mindmapData.node_count || 0,
        creation_time: new Date().toISOString()
      },
      session_id: this.sessionId
    });

    this.updateDailyActivity({ mindmaps_created: 1 });
  }

  // Track file upload
  trackFileUpload(fileData: any): void {
    // Log debug info for localhost testing
    console.log('⬆️ GA4 Upload Event:', {
      fileData,
      hostname: window.location.hostname,
      ga4Loaded: this.isGA4Loaded
    });

    // GA4 custom event
    if (this.isGA4Loaded) {
      gtag('event', 'file_upload', {
        event_category: 'engagement',
        event_label: fileData.type,
        value: Math.round(fileData.size / 1024), // Size in KB
        session_id: this.sessionId,
        // Add custom parameters for better tracking
        file_type: fileData.type,
        bundle_id: fileData.bundle_id,
        user_type: fileData.user_type,
        operation: fileData.operation
      });
      
      console.log('✅ GA4 file_upload event sent:', {
        file_type: fileData.type,
        bundle_id: fileData.bundle_id,
        user_type: fileData.user_type,
        operation: fileData.operation
      });
    }

    // Custom tracking
    this.trackCustomEvent({
      event_type: 'file_upload',
      event_data: {
        file_name: fileData.name,
        file_type: fileData.type,
        file_size: fileData.size,
        upload_time: new Date().toISOString(),
        operation: fileData.operation || 'upload',
        bundle_id: fileData.bundle_id,
        bundle_index: fileData.bundle_index,
        user_type: fileData.user_type,
        error_message: fileData.error_message
      },
      session_id: this.sessionId
    });

    this.updateDailyActivity({ files_uploaded: 1 });
  }

  // Track file download
  trackFileDownload(fileData: any): void {
    // Log debug info for localhost testing
    console.log('🔽 GA4 Download Event:', {
      fileData,
      hostname: window.location.hostname,
      ga4Loaded: this.isGA4Loaded,
      measurementId: 'G-4S9545945' // Your measurement ID
    });

    // GA4 custom event
    if (this.isGA4Loaded) {
      gtag('event', 'file_download', {
        event_category: 'engagement',
        event_label: fileData.type,
        value: Math.round(fileData.size / 1024), // Size in KB
        session_id: this.sessionId,
        // Add custom parameters for better tracking
        file_type: fileData.type,
        bundle_id: fileData.bundle_id,
        user_type: fileData.user_type,
        operation: fileData.operation
      });
      
      console.log('✅ GA4 file_download event sent:', {
        file_type: fileData.type,
        bundle_id: fileData.bundle_id,
        user_type: fileData.user_type,
        operation: fileData.operation
      });
    }

    // Custom tracking
    this.trackCustomEvent({
      event_type: 'file_download',
      event_data: {
        file_name: fileData.name,
        file_type: fileData.type,
        file_size: fileData.size,
        download_time: new Date().toISOString(),
        operation: fileData.operation || 'download',
        bundle_id: fileData.bundle_id,
        bundle_index: fileData.bundle_index,
        user_type: fileData.user_type,
        error_message: fileData.error_message
      },
      session_id: this.sessionId
    });
  }

  // Track file delete
  trackFileDelete(fileData: any): void {
    // Log debug info for localhost testing
    console.log('🗑️ GA4 Delete Event:', {
      fileData,
      hostname: window.location.hostname,
      ga4Loaded: this.isGA4Loaded
    });

    // GA4 custom event
    if (this.isGA4Loaded) {
      gtag('event', 'file_delete', {
        event_category: 'engagement',
        event_label: fileData.type,
        value: 1,
        session_id: this.sessionId,
        // Add custom parameters for better tracking
        file_type: fileData.type,
        bundle_id: fileData.bundle_id,
        user_type: fileData.user_type,
        operation: fileData.operation
      });
      
      console.log('✅ GA4 file_delete event sent:', {
        file_type: fileData.type,
        bundle_id: fileData.bundle_id,
        user_type: fileData.user_type,
        operation: fileData.operation
      });
    }

    // Custom tracking
    this.trackCustomEvent({
      event_type: 'file_delete',
      event_data: {
        file_name: fileData.name,
        file_type: fileData.type,
        delete_time: new Date().toISOString(),
        operation: fileData.operation || 'delete',
        bundle_id: fileData.bundle_id,
        bundle_index: fileData.bundle_index,
        user_type: fileData.user_type,
        error_message: fileData.error_message
      },
      session_id: this.sessionId
    });
  }

  // Track file replace
  trackFileReplace(fileData: any): void {
    // GA4 custom event
    if (this.isGA4Loaded) {
      gtag('event', 'file_replace', {
        event_category: 'engagement',
        event_label: fileData.type,
        value: Math.round(fileData.size / 1024), // Size in KB
        session_id: this.sessionId
      });
    }

    // Custom tracking
    this.trackCustomEvent({
      event_type: 'file_replace',
      event_data: {
        file_name: fileData.name,
        file_type: fileData.type,
        file_size: fileData.size,
        replace_time: new Date().toISOString(),
        operation: fileData.operation || 'replace',
        bundle_id: fileData.bundle_id,
        bundle_index: fileData.bundle_index,
        user_type: fileData.user_type,
        error_message: fileData.error_message
      },
      session_id: this.sessionId
    });
  }

  // Track quiz completion
  trackQuizCompleted(quizData: any): void {
    // GA4 custom event
    if (this.isGA4Loaded) {
      gtag('event', 'quiz_complete', {
        event_category: 'engagement',
        event_label: quizData.topic,
        value: quizData.score,
        session_id: this.sessionId
      });
    }

    // Custom tracking
    this.trackCustomEvent({
      event_type: 'quiz_completed',
      event_data: {
        quiz_id: quizData.id,
        topic: quizData.topic,
        score: quizData.score,
        total_questions: quizData.total_questions,
        time_taken: quizData.time_taken,
        completion_time: new Date().toISOString()
      },
      session_id: this.sessionId
    });
  }

  // Track search
  trackSearch(searchTerm: string, resultsCount: number): void {
    // GA4 search event
    if (this.isGA4Loaded) {
      gtag('event', 'search', {
        search_term: searchTerm,
        results_count: resultsCount,
        session_id: this.sessionId
      });
    }

    // Custom tracking
    this.trackCustomEvent({
      event_type: 'search',
      event_data: {
        search_term: searchTerm,
        results_count: resultsCount,
        search_time: new Date().toISOString()
      },
      session_id: this.sessionId
    });
  }

  // Track form interactions
  trackFormStart(formName: string): void {
    if (this.isGA4Loaded) {
      gtag('event', 'form_start', {
        form_name: formName,
        session_id: this.sessionId
      });
    }
  }

  trackFormSubmit(formName: string, success: boolean): void {
    if (this.isGA4Loaded) {
      gtag('event', 'form_submit', {
        form_name: formName,
        success: success,
        session_id: this.sessionId
      });
    }
  }

  // Track custom event to database
  private trackCustomEvent(eventData: CustomEventData): void {
    const user = this.authService.getUserInfo();
    if (!user) {
      console.warn('Analytics: No user found, skipping event tracking');
      return;
    }

    console.log('🔍 Analytics Debug: Full user object:', user);
    console.log('🔍 Analytics Debug: Available user properties:', Object.keys(user));

    // Get the correct user ID for backend
    const userId = this.getUserIdForBackend();
    if (!userId) {
      console.error('Analytics: Could not determine user ID');
      return;
    }

    console.log('🔍 Analytics Debug: Final userId being sent:', userId);
    console.log('🔍 Analytics Debug: User email:', user.email);

    const payload = {
      user_id: userId,
      event_type: eventData.event_type,
      event_data: eventData.event_data || {},
      session_id: eventData.session_id || this.sessionId,
      page_url: eventData.page_url || window.location.pathname
    };

    console.log('🔍 Analytics Debug: Complete payload being sent:', payload);

    this.apiService.post('/analytics/events', payload).subscribe({
      next: (response: any) => {
        console.log('✅ Analytics event tracked successfully:', response);
      },
      error: (error: any) => {
        console.error('❌ Analytics event failed:', error);
        console.error('❌ Error details:', error.error);
        
        // Try using email as user_id if current fails and we haven't tried email yet
        if (error.error?.error === 'Insufficient permissions' && user.email && userId !== user.email) {
          console.log('🔄 Retrying with email as user_id...');
          const emailPayload = { ...payload, user_id: user.email };
          console.log('🔄 Email payload:', emailPayload);
          
          this.apiService.post('/analytics/events', emailPayload).subscribe({
            next: (response: any) => {
              console.log('✅ Analytics event tracked with email:', response);
            },
            error: (emailError: any) => {
              console.error('❌ Email retry also failed:', emailError);
            }
          });
        }
      }
    });
  }

  // Start user session
  private startUserSession(): void {
    const userId = this.getUserIdForBackend();
    if (!userId) return;

    const sessionData = {
      user_id: userId,
      session_id: this.sessionId,
      start_time: this.currentSessionStartTime.toISOString()
    };

    this.apiService.post('/analytics/sessions', sessionData).subscribe({
      next: (response: any) => {
        console.log('Session started:', response);
      },
      error: (error: any) => {
        console.error('Failed to start session:', error);
      }
    });
  }

  // End user session
  endUserSession(): void {
    const user = this.authService.getUserInfo();
    if (!user) return;

    const sessionDuration = Math.round((new Date().getTime() - this.currentSessionStartTime.getTime()) / 60000); // minutes

    const sessionData = {
      session_id: this.sessionId,
      end_time: new Date().toISOString(),
      page_count: this.pageViewCount
    };

    this.apiService.put(`/analytics/sessions/${this.sessionId}`, sessionData).subscribe({
      next: (response: any) => {
        console.log('Session ended:', response);
      },
      error: (error: any) => {
        console.error('Failed to end session:', error);
      }
    });

    this.updateDailyActivity({ 
      session_count: 1, 
      page_views: this.pageViewCount,
      time_spent_minutes: sessionDuration
    });
  }

  // Update daily activity summary
  private updateDailyActivity(activity: UserActivity): void {
    const userId = this.getUserIdForBackend();
    if (!userId) return;

    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    
    const payload = {
      user_id: userId,
      activity_date: today,
      ...activity
    };

    this.apiService.post('/analytics/daily-activity', payload).subscribe({
      next: (response: any) => {
        console.log('Daily activity updated:', response);
      },
      error: (error: any) => {
        console.error('Failed to update daily activity:', error);
      }
    });
  }

  // Get analytics dashboard data
  getAnalyticsDashboard(startDate: string, endDate: string) {
    const userId = this.getUserIdForBackend();
    if (!userId) return;

    return this.apiService.get(`/analytics/dashboard?start_date=${startDate}&end_date=${endDate}&user_id=${userId}`);
  }

  // Get user activity summary
  getUserActivitySummary(days: number = 30) {
    const userId = this.getUserIdForBackend();
    if (!userId) return;

    return this.apiService.get(`/analytics/user-activity?days=${days}&user_id=${userId}`);
  }

  // Get connection status for validation
  getConnectionStatus(): { ga4: boolean, database: boolean, sessionId: string } {
    return {
      ga4: this.isGA4Loaded && typeof gtag === 'function',
      database: true, // Assuming database is available if service is loaded
      sessionId: this.sessionId
    };
  }

  // Debug method to check what user ID the backend expects
  debugUserIdInBackend() {
    const user = this.authService.getUserInfo();
    if (!user) {
      console.log('❌ No user info available');
      return;
    }

    console.log('🔍 DEBUG: User info from frontend:', {
      id: user.id,
      sub: user.sub,
      google_sub: user.google_sub,
      email: user.email,
      all_keys: Object.keys(user)
    });

    // Try to get current user info from backend
    this.apiService.get('/users/me').subscribe({
      next: (response: any) => {
        console.log('✅ Backend user info:', response);
        console.log('✅ Backend user ID field:', response.id || response.user_id || 'NO_ID_FIELD');
      },
      error: (error: any) => {
        console.error('❌ Could not get backend user info:', error);
        
        // Try alternate endpoint
        this.apiService.get('/auth/user').subscribe({
          next: (response: any) => {
            console.log('✅ Backend auth user info:', response);
            console.log('✅ Backend auth user ID field:', response.id || response.user_id || 'NO_ID_FIELD');
          },
          error: (error: any) => {
            console.error('❌ Could not get backend auth user info:', error);
          }
        });
      }
    });
  }
} 